import React from 'react';
import { toast } from 'react-toastify';
import { GetApiKeyDto } from 'services/Dtos/ApiKeyDtos';

interface ApiKeyDetailsTabProps {
  apiKey: GetApiKeyDto;
  onToggleGemini: () => Promise<void>;
  togglingGemini: boolean;
}

const ApiKeyDetailsTab: React.FC<ApiKeyDetailsTabProps> = ({
  apiKey,
  onToggleGemini,
  togglingGemini
}) => {
  const copyToClipboard = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        toast.success("API key copied to clipboard!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
        toast.error("Failed to copy API key", {
          position: "top-right",
          autoClose: 3000,
        });
      });
  };

  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        {/* API Key Name Section */}
        <div className="mb-4">
          <h6 className="text-muted mb-3">
            <i className="bi bi-tag me-2"></i>
            Name
          </h6>
          <p className="fs-5 fw-semibold mb-0">{apiKey.name}</p>
        </div>

        <hr className="my-4" />

        {/* API Key Section */}
        <div className="mb-4">
          <h6 className="text-muted mb-3">
            <i className="bi bi-key me-2"></i>
            API Key
          </h6>
          <div className="input-group">
            <input
              type="text"
              className="form-control"
              value={apiKey.key}
              readOnly
              aria-label="API Key"
            />
            <button
              className="btn btn-outline-secondary"
              type="button"
              onClick={() => copyToClipboard(apiKey.key)}
              title="Copy to clipboard"
            >
              <i className="bi bi-clipboard"></i>
            </button>
          </div>
        </div>

        <hr className="my-4" />

        {/* Contact Information Section */}
        <div className="mb-4">
          <h6 className="text-muted mb-3">
            <i className="bi bi-person-lines-fill me-2"></i>
            Contact Information
          </h6>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label text-muted small">Phone</label>
              <p className="mb-0">{apiKey.phone || '-'}</p>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label text-muted small">Email</label>
              <p className="mb-0">{apiKey.email || '-'}</p>
            </div>
          </div>
        </div>

        <hr className="my-4" />

        {/* Additional Information Section */}
        <div className="mb-4">
          <h6 className="text-muted mb-3">
            <i className="bi bi-file-text me-2"></i>
            Additional Information
          </h6>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label text-muted small">Description</label>
              <p className="mb-0">{apiKey.description || '-'}</p>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label text-muted small">Number of Configurations</label>
              <p className="mb-0">
                <span className="badge bg-primary fs-6">{apiKey.apiKeyConfigs.length}</span>
              </p>
            </div>
          </div>
        </div>

        <hr className="my-4" />

        {/* Settings Section */}
        <div>
          <h6 className="text-muted mb-3">
            <i className="bi bi-gear me-2"></i>
            Settings
          </h6>
          <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded">
            <div>
              <p className="mb-1 fw-semibold">Gemini Integration</p>
              <p className="mb-0 text-muted small">Enable or disable Gemini AI for this API key</p>
            </div>
            <button
              className={`btn ${apiKey.shouldUseGemini ? 'btn-success' : 'btn-outline-secondary'}`}
              onClick={onToggleGemini}
              disabled={togglingGemini}
              title={apiKey.shouldUseGemini ? 'Gemini enabled' : 'Gemini disabled'}
            >
              {togglingGemini ? (
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              ) : (
                <i className="bi bi-robot me-2"></i>
              )}
              {apiKey.shouldUseGemini ? 'Enabled' : 'Disabled'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiKeyDetailsTab;
